"use client";

import { useEffect } from "react";

import { track } from "@/lib/analytics/track";

const OFFER_PATHS = new Set(["/contact", "/scan", "/consult", "/consulting", "/hire", "/local", "/products"]);
const OFFER_HASHES = new Set(["#order", "#prices", "#packages", "#offers"]);
const OUTBOUND_HOSTS = ["servbuilder.com", "github.com", "linkedin.com", "twitter.com", "x.com"];

function locationFor(element: Element): string {
  const marked = element.closest("[data-cta-location]");
  if (marked instanceof HTMLElement && marked.dataset.ctaLocation) return marked.dataset.ctaLocation;
  if (element.closest("footer")) return "footer";
  if (element.closest("header")) return "header";
  const section = element.closest("section[id]");
  if (section instanceof HTMLElement && section.id) return section.id;
  if (element.closest(".page-hero")) return "hero";
  return "page";
}

function hostMatches(hostname: string): string | null {
  const host = hostname.replace(/^www\./, "");
  for (const allowed of OUTBOUND_HOSTS) {
    if (host === allowed || host.endsWith(`.${allowed}`)) return host;
  }
  return null;
}

/**
 * One listener for CTA clicks and a short outbound list. Checkout and booking
 * clicks are tracked on the buttons themselves so they can carry a price.
 */
export default function AnalyticsClicks() {
  useEffect(() => {
    function onClick(event: MouseEvent) {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const anchor = target.closest("a");
      if (!(anchor instanceof HTMLAnchorElement)) return;
      const href = anchor.getAttribute("href");
      if (!href || href.startsWith("mailto:")) return;

      let url: URL;
      try {
        url = new URL(href, window.location.href);
      } catch {
        return;
      }

      const outbound = hostMatches(url.hostname);
      if (outbound && url.origin !== window.location.origin) {
        track("outbound_click", { link_domain: outbound, location: locationFor(anchor) });
        return;
      }

      const isCta = anchor.classList.contains("btn") || anchor.hasAttribute("data-cta");
      if (!isCta) return;
      if (anchor.getAttribute("aria-disabled") === "true" || anchor.classList.contains("is-disabled")) return;
      if (url.origin !== window.location.origin) return;

      const path = url.pathname.replace(/\/$/, "") || "/";
      const hash = url.hash;
      if (!OFFER_PATHS.has(path) && !OFFER_HASHES.has(hash)) return;

      const label = (anchor.textContent || "").replace(/\s+/g, " ").trim().slice(0, 80);
      track("cta_click", {
        location: locationFor(anchor),
        label,
        destination: `${path}${url.search}${hash}`,
        page_path: window.location.pathname,
      });
    }

    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  return null;
}
